import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";
import emailjs from "emailjs-com";
import { toast } from "react-toastify";

const DeleteAccount = () => {
  const [form, setForm] = useState({
    email: "",
    reason: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!form.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    if (!form.reason.trim()) {
      toast.error("Please provide a reason for account deletion");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    if (formRef.current) {
      emailjs
        .sendForm(
          "service_3d5bhy6", // Replace with your service ID
          "template_zgr5d3a", // Replace with your template ID for delete account requests
          formRef.current,
          "C0NxhAb6pQ6oVjvSY" // Replace with your public key
        )
        .then(
          (result) => {
            toast.success("Account deletion request submitted successfully! We will process your request within 7 business days.");
            setForm({
              email: "",
              reason: "",
            });
            setIsSubmitting(false);
          },
          (error) => {
            toast.error("Failed to submit request. Please try again or contact support.");
            console.error("EmailJS Error:", error);
            setIsSubmitting(false);
          }
        );
    }
  };

  return (
    <div className="pt-24 bg-night min-h-screen">
      {/* Hero Section */}
      <section className="relative flex flex-col items-center text-center px-6 py-12 bg-night-warm border-b border-night-line">
        <motion.h1
          className="font-display text-4xl md:text-6xl font-extrabold mb-4 leading-tight text-mist"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          DELETE ACCOUNT
        </motion.h1>
        <motion.div
          className="flex flex-col items-center space-y-2 text-mist-dim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-lg font-semibold">Request Account Deletion</p>
          <p className="text-base">Platform: Zuntly Commerce System</p>
        </motion.div>
      </section>

      {/* Delete Account Content */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          className="space-y-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {/* Important Notice */}
          <div className="bg-night-warm rounded-2xl shadow-md p-8 border-2 border-night-line">
            <h2 className="text-2xl md:text-3xl font-bold text-red-900 mb-4 flex items-center">
              <span className="mr-3 text-3xl">⚠️</span>
              Important Notice
            </h2>
            <div className="text-mist space-y-4 leading-relaxed">
              <p className="font-semibold text-red-800">
                Account deletion is permanent and irreversible. Please read carefully before proceeding.
              </p>
            </div>
          </div>

          {/* What Will Be Deleted */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">🗑️</span>
              Data That Will Be Permanently Deleted
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p className="mb-4 font-medium">
                When you delete your account, all of the following data will be permanently removed from our systems:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Account Information</p>
                    <p className="text-sm text-mist-soft">Your profile, credentials, and all personal information associated with your account</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Order History & Data</p>
                    <p className="text-sm text-mist-soft">All your past orders, order details, invoices, and transaction history</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Branch Data (for Merchants)</p>
                    <p className="text-sm text-mist-soft">All branch information, store configurations, and branch-related data</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Product & Inventory Data (for Merchants)</p>
                    <p className="text-sm text-mist-soft">All products, inventory records, stock levels, and product configurations</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Customer Data (for Merchants)</p>
                    <p className="text-sm text-mist-soft">All customer information, addresses, and purchase history associated with your store</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Store Customizations (for Merchants)</p>
                    <p className="text-sm text-mist-soft">All store themes, banners, sections, customizations, and branding elements</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Reviews & Ratings</p>
                    <p className="text-sm text-mist-soft">All reviews you've written or received, ratings, and feedback</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Coupons & Promotions (for Merchants)</p>
                    <p className="text-sm text-mist-soft">All coupon codes, promotional campaigns, and discount configurations</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">Payment & Transaction Records</p>
                    <p className="text-sm text-mist-soft">All payment history, transaction details, and financial records (except those required by law)</p>
                  </div>
                </div>

                <div className="flex items-start bg-night-warm p-4 rounded-lg border border-night-line">
                  <span className="text-glow-deep mr-3 text-xl mt-1">❌</span>
                  <div>
                    <p className="font-semibold text-mist">App Access & Preferences</p>
                    <p className="text-sm text-mist-soft">Your saved preferences, settings, and access to all Zuntly-powered applications</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 mt-6">
                <p className="font-bold text-yellow-900 text-lg mb-2">⚠️ This Action Cannot Be Undone</p>
                <p className="text-yellow-800">
                  Once your account is deleted, there is no way to recover your data. All information will be permanently erased from our systems within 30 days of deletion.
                </p>
              </div>
            </div>
          </div>

          {/* Before You Proceed */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📋</span>
              Before You Proceed
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p className="mb-4">Please consider the following before deleting your account:</p>
              
              <ul className="list-disc list-inside ml-4 space-y-3">
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span><strong>Export Your Data:</strong> Download any important information, invoices, or reports you may need in the future</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span><strong>Cancel Subscriptions:</strong> Ensure all active subscriptions or pending payments are settled</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span><strong>Pending Orders:</strong> Complete or cancel any pending orders before deletion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span><strong>Alternative Solutions:</strong> Consider temporarily deactivating your account instead of permanent deletion</span>
                </li>
              </ul>

              <div className="bg-night-warm border border-night-line rounded-lg p-4 mt-4">
                <p className="font-semibold text-mist">
                  Need help? Contact our support team at <a href="mailto:ecom@zuntly.com" className="underline">ecom@zuntly.com</a> before deleting your account.
                </p>
              </div>
            </div>
          </div>

          {/* Delete Account Request Form */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📝</span>
              Submit Deletion Request
            </h2>
            <div className="text-mist-soft space-y-6 leading-relaxed">
              <p className="mb-4">
                To proceed with account deletion, please fill out the form below. Our team will review your request and process it within <strong>7 business days</strong>.
              </p>

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-mist mb-2">
                    Email Address <span className="text-glow-deep">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your registered email address"
                    required
                    className="w-full px-4 py-3 border border-night-line rounded-lg focus:outline-none focus:ring-2 focus:ring-glow focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Please provide the email address associated with your Zuntly account
                  </p>
                </div>

                {/* Reason Field */}
                <div>
                  <label htmlFor="reason" className="block text-sm font-semibold text-mist mb-2">
                    Reason for Deletion <span className="text-glow-deep">*</span>
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    placeholder="Please tell us why you want to delete your account. Your feedback helps us improve."
                    rows="5"
                    required
                    className="w-full px-4 py-3 border border-night-line rounded-lg focus:outline-none focus:ring-2 focus:ring-glow focus:border-transparent transition-all resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 10 characters required
                  </p>
                </div>

                {/* Confirmation Checkbox */}
                <div className="bg-night-warm border-2 border-red-300 rounded-lg p-6">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="confirm"
                      required
                      className="mt-1 mr-3 w-5 h-5 text-glow-deep border-night-line rounded focus:ring-glow"
                    />
                    <label htmlFor="confirm" className="text-sm text-mist">
                      <span className="font-bold">I understand that:</span>
                      <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                        <li>This action is permanent and cannot be undone</li>
                        <li>All my data will be permanently deleted</li>
                        <li>I will lose access to my account and all associated services</li>
                        <li>I have exported any data I need to keep</li>
                      </ul>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-lg shadow-lg transition-all duration-300 ${
                      isSubmitting
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:from-red-700 hover:to-red-800 hover:shadow-xl transform hover:-translate-y-1"
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting Request...
                      </span>
                    ) : (
                      "Submit Deletion Request"
                    )}
                  </button>
                  <a
                    href="/"
                    className="flex-1 px-8 py-4 bg-gray-200 text-mist font-bold rounded-lg shadow-lg hover:bg-gray-300 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 text-center"
                  >
                    Cancel
                  </a>
                </div>
              </form>

              <div className="bg-night-warm border border-night-line rounded-lg p-6 mt-6">
                <p className="font-semibold text-mist mb-2">📧 What Happens Next?</p>
                <ol className="list-decimal list-inside space-y-2 text-mist-soft ml-2">
                  <li>You will receive a confirmation email acknowledging your request</li>
                  <li>Our team will review your request within 1-2 business days</li>
                  <li>We may contact you if additional verification is needed</li>
                  <li>Your account will be permanently deleted within 7 business days</li>
                  <li>You will receive a final confirmation email once deletion is complete</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Legal Information */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">⚖️</span>
              Legal Information
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>
                <strong>Data Retention for Legal Compliance:</strong> While we delete most of your data immediately, 
                certain information may be retained for up to 7 years as required by Indian tax laws and regulations, including:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Transaction records required for tax purposes</li>
                <li>Financial documents as per GST compliance</li>
                <li>Data required for legal disputes or investigations</li>
              </ul>
              <p className="mt-4">
                This data is stored securely and will only be accessed if legally required.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-night-warm/50 rounded-2xl p-8 border border-night-line">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-mist mb-4 flex items-center">
              <span className="mr-3 text-3xl">📧</span>
              Need Help?
            </h2>
            <div className="text-mist-soft space-y-4 leading-relaxed">
              <p>For any questions or concerns about account deletion:</p>
              <div className="bg-night-warm rounded-lg p-6 border border-night-line space-y-2">
                <p className="font-bold text-mist text-lg">Zuntly Commerce System</p>
                <p className="font-semibold text-mist-soft">ZUNVENT SOLUTIONS PRIVATE LIMITED</p>
                <p className="text-mist-soft flex items-start">
                  <span className="mr-2">📍</span>
                  <span>9-418/A, Nuzividu Road, ST Colony, Vissannapet, Krishna, Vissannapet, Andhra Pradesh, India, 521215</span>
                </p>
                <p className="text-mist-soft flex items-center">
                  <span className="mr-2">📧</span>
                  <span>Email: ecom@zuntly.com</span>
                </p>
                <p className="text-mist-soft flex items-center">
                  <span className="mr-2">🌐</span>
                  <span>Website: <a href="https://zuntly.com" className="underline" target="_blank" rel="noopener noreferrer">www.zuntly.com</a></span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DeleteAccount;
